import { Box, Button, TextField, useMediaQuery } from '@mui/material';
import Header from '../../components/Header';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DataGrid } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import { useCookiesLogin } from '../../config/cookies';
import RegisterBusiness from '../registerBusinessForm';
import { HOST_REDUTO, HOST_API } from '../../config/hosts';
import { useTranslation } from '../../hooks/useTranslation';

const phoneRegex = /^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/;

const phoneColumns = [
  { field: 'id', headerName: 'ID', flex: 0.2 },
  { field: 'phone', headerName: 'Telefone', flex: 0.8 },
];

const initialValues = {
  email: '',
  phone: '',
  cep: '',
};

const userSchema = (t) =>
  yup.object().shape({
    email: yup.string().email(t('messages.invalidEmail')),
    phone: yup.string().matches(phoneRegex, t('messages.enterValidPhone')),
    cep: yup.string().required(t('messages.cepRequired')),
  });

const Form = () => {
  const [consultaRealizada, setConsultaRealizada] = useState(false);
  const [dataInfo, setDataInfo] = useState(false);
  const [officialName, setOfficialName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [city, setCity] = useState('');
  const [phoneList, setPhoneList] = useState([]);
  const [cnpj, setCnpj] = useState('');
  const [showConsult, setShowConsult] = useState(false);
  const { t } = useTranslation();

  const { cookies } = useCookiesLogin();
  const { id: userId, role } = cookies.AUTH_TOKEN;

  const handleSerasa = async () => {
    if (cnpj === '') {
      toast.error(t('messages.enterValidCnpj'));
      return;
    }

    try {
      const request = await fetch(`${HOST_REDUTO}/consults/serasa/${cnpj}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: userId }),
      });

      if (!request.ok) {
        throw new Error(t('messages.consultError'));
      }

      const data = await request.json();
      setConsultaRealizada(true);
      setDataInfo(data);
      setOfficialName(data.razao_social || '');
      setTradeName(data.nome_fantasia || '');
      setCity(data.municipio || '');
      toast.success(t('messages.consultSuccess'));
    } catch (error) {
      console.error(error);
      toast.error(t('messages.consultError'));
    }
  };

  const DataInfo = () => {
    if (!consultaRealizada || !dataInfo) return null;

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={userSchema(t)}
        onSubmit={async (values, { resetForm }) => {
          try {
            const request = await fetch(`${HOST_API}/v1/business/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                cnpj,
                cep: values.cep,
                city: city || t('messages.noInformation'),
                email: values.email,
                name: tradeName === '' ? officialName : tradeName,
                phone: phoneList.map((p) => p.phone).toString(),
                brain_id: dataInfo.idConsult,
                token_brain: dataInfo.tokenSerasa,
                user_id: userId, // quem cadastrou
              }),
            });

            if (!request.ok) throw new Error(t('messages.companyRegisterError'));

            resetForm();
            setPhoneList([]);
            toast.success(t('messages.companyRegistered'));
          } catch (error) {
            console.error(error);
            toast.error(t('messages.companyRegisterError'));
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                '& > div': { gridColumn: 'span 4' },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label={t('forms.companyName')}
                value={officialName}
                disabled
                sx={{ gridColumn: 'span 2' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label={t('forms.tradeName')}
                value={tradeName}
                disabled
                sx={{ gridColumn: 'span 2' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label={t('forms.city')}
                value={city}
                disabled
                sx={{ gridColumn: 'span 2' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="email"
                label={t('forms.email')}
                onChange={handleChange}
                value={values.email}
                name="email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: 'span 2' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label={t('forms.zipCode')}
                onChange={handleChange}
                value={values.cep}
                name="cep"
                error={!!touched.cep && !!errors.cep}
                helperText={touched.cep && errors.cep}
                sx={{ gridColumn: 'span 2' }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label={t('forms.phone')}
                onChange={handleChange}
                value={values.phone}
                name="phone"
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: 'span 2' }}
              />

              <Button
                color="secondary"
                variant="contained"
                onClick={() => {
                  if (!values.phone || errors.phone) {
                    toast.error(t('messages.enterValidPhone'));
                    return;
                  }
                  setPhoneList((prev) => [
                    ...prev,
                    {
                      id: prev.length + 1,
                      phone: values.phone.replace(/\D/g, ''),
                    },
                  ]);
                  setFieldValue('phone', '');
                }}
              >
                <Add />
              </Button>

              <Box gridColumn="span 4" height="200px" width="100%" mt="20px">
                <DataGrid
                  rows={phoneList}
                  columns={phoneColumns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" mt="20px">
              <Button
                type="button"
                color="warning"
                variant="contained"
                onClick={() => {
                  setConsultaRealizada(false);
                  setDataInfo(false);
                  setCnpj('');
                  setOfficialName('');
                  setTradeName('');
                  setCity('');
                  setPhoneList([]);
                }}
              >
                {t('buttons.newConsult')}
              </Button>

              {['super_admin', 'admin', 'consultant', 'representative'].includes(role) && (
                <Button type="submit" color="secondary" variant="contained">
                  {t('buttons.registerDealer')}
                </Button>
              )}
            </Box>
          </form>
        )}
      </Formik>
    );
  };

  return (
    <Box m="20px">
      <ToastContainer />
      <Header
        title={t('titles.companyRegistration')}
        subtitle={t('titles.companyRegistrationSubtitle')}
      />

      {role === 'representative' ? (
        <RegisterBusiness />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent={'flex-start'}
            alignItems="center"
            gap="20px"
            mb="20px"
          >
            <TextField
              label={t('forms.enterCnpj')}
              variant="outlined"
              value={cnpj}
              onChange={(e) => setCnpj(e.target.value)}
              sx={{ minWidth: '300px' }}
            />
            <Button color="secondary" variant="contained" onClick={handleSerasa}>
              {t('buttons.consultCnpj')}
            </Button>
            <Button color="primary" variant="outlined" onClick={() => setShowConsult(!showConsult)}>
              {showConsult ? t('buttons.registerWithoutConsult') : t('buttons.consultCnpj')}
            </Button>
          </Box>

          {consultaRealizada && dataInfo && <DataInfo />}
          {!consultaRealizada && showConsult && <RegisterBusiness />}
        </>
      )}
    </Box>
  );
};

export default Form;
